import { getActiveOrgId } from '../stores/orgStore';
import { apiFetch } from '@/lib/api';

export class OrgService {
  private readonly baseUrl: string;

  constructor() {
    const vURL = (import.meta as any).env?.VITE_API_URL as string | undefined;
    if (vURL) this.baseUrl = vURL.replace(/\/$/, ''); else this.baseUrl = '';
  }

  private headers(extra?: Record<string,string>) {
    const orgId = getActiveOrgId();
    return {
      ...(extra || {}),
      ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    } as Record<string,string>;
  }

  async listOrgs() {
    const res = await apiFetch(`${this.baseUrl}/api/orgs`);
    if (!res.ok) throw new Error('Failed to load orgs');
    const payload = await res.json();
    return payload?.data || [];
  }

  async markOnboardingComplete(orgId: string): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/onboarding/complete`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to mark onboarding complete');
  }

  async listMembers(orgId: string) {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/members`);
    if (!res.ok) throw new Error('Failed to load members');
    const payload = await res.json();
    return payload?.data || [];
  }

  async invite(orgId: string, email: string, role: 'admin'|'member') {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    });
    if (!res.ok) throw new Error('Failed to create invite');
    const payload = await res.json();
    return payload?.data;
  }

  async updateMemberRole(orgId: string, userId: string, role: 'owner'|'admin'|'member') {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/members/${userId}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role })
    });
    if (!res.ok) throw new Error('Failed to update role');
  }

  async removeMember(orgId: string, userId: string) {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/members/${userId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to remove member');
  }

  async listInvites(orgId: string) {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/invites`);
    if (!res.ok) throw new Error('Failed to load invites');
    const payload = await res.json();
    return payload?.data || [];
  }

  async revokeInvite(orgId: string, inviteId: string) {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/invites/${inviteId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to revoke invite');
  }

  async resendInvite(orgId: string, inviteId: string) {
    const res = await apiFetch(`${this.baseUrl}/api/orgs/${orgId}/invites/${inviteId}/resend`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to resend invite');
  }

  async resolveInvite(token: string): Promise<{ status: 'valid'|'invalid'|'expired'|'accepted'; email?: string; role?: 'admin'|'member'; organization?: { id: string; name: string } | null; userExists?: boolean; hasPassword?: boolean; }> {
    const res = await apiFetch(`${this.baseUrl}/api/invites/resolve?token=${encodeURIComponent(token)}`);
    if (!res.ok) throw new Error('Failed to resolve invite');
    const payload = await res.json();
    return payload?.data;
  }

  async acceptInvite(token: string, fullName?: string, password?: string): Promise<{ user?: any; autoLoggedIn?: boolean }> {
    const res = await apiFetch(`${this.baseUrl}/api/invites/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, fullName, password }),
    });
    if (!res.ok) {
      let message = 'Failed to accept invite';
      try {
        const payload = await res.json();
        message = payload?.error?.message || message;
      } catch {}
      throw new Error(message);
    }
    const payload = await res.json();
    return payload?.data || {};
  }
}


